import * as React from "react"

import Icon from "@/components/Icon"
import { cn } from "@/helpers/classNames"

function Card({
  className,
  ref,
  ...props
}: { ref?: React.Ref<HTMLDivElement> } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border bg-card text-card-foreground shadow-xs",
        className,
      )}
      {...props}
    />
  )
}

function CardHeader({
  className,
  ref,
  ...props
}: { ref?: React.Ref<HTMLDivElement> } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      ref={ref}
      className={cn("flex flex-col space-y-1.5 p-6", className)}
      {...props}
    />
  )
}

function CardTitle({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLParagraphElement>
} & React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      ref={ref}
      className={cn(
        "text-2xl leading-none font-semibold tracking-tight",
        className,
      )}
      {...props}
    />
  )
}

function CardDescription({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLParagraphElement>
} & React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

function CardContent({
  className,
  ref,
  ...props
}: { ref?: React.Ref<HTMLDivElement> } & React.HTMLAttributes<HTMLDivElement>) {
  return <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
}

function CardFooter({
  className,
  ref,
  ...props
}: { ref?: React.Ref<HTMLDivElement> } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      ref={ref}
      className={cn("flex items-center p-6 pt-0", className)}
      {...props}
    />
  )
}

/** Used to display error messages at the top of the Call Panel view, not part of shadcn/ui */
function ErrorCard({
  children,
  className,
  heading,
  onClose,
}: React.PropsWithChildren<{
  className?: string
  heading: React.ReactNode
  onClose?: () => void
}>) {
  return (
    <div
      className={cn(
        `
          flex flex-col gap-y-2 rounded-sm border border-danger-200
          bg-danger-100 px-4 py-3 font-body-small-regular text-neutral-600
        `,
        className,
      )}
    >
      <div className="flex flex-row flex-wrap items-center gap-x-2.5">
        <Icon name="alert" className="text-danger-300" />
        <div className="grow font-body-small-bold" data-unmask>
          {heading}
        </div>
        {onClose && ( // TODO it should be a button, not a div, for accessibility!
          <div className="cursor-pointer" onClick={onClose}>
            <Icon name="close" className="text-danger-200" />
          </div>
        )}
      </div>
      <>{children}</>
    </div>
  )
}

export {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  ErrorCard,
}
